import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Error } from '@material-ui/icons';
import gql from 'graphql-tag';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';

import { memberClient } from '../../config/Apollo';
import { DAY_NAMES } from '../../constants';
import { AvailableHourRange } from '../../models/Sessions';
import { colors } from '../../themes/colors';
import MHButton from '../MHButton';
import AvailableDayInput from './AvailableDayInput';

const useStyles = makeStyles({
  title: {
    fontFamily: 'Avenir-Black',
    fontSize: 28,
    color: colors.black4,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  explanationText: {
    fontSize: 14,
    color: colors.black2,
  },
  daysList: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
  },
  notificationWrapper: {
    display: 'flex',
    margin: '15px auto -10px 24px',
    padding: '4px 10px',
    borderRadius: 100,
    alignItems: 'center',
  },
  errorTextWrapper: {
    backgroundColor: 'rgba(255, 161, 80, 0.2)',
  },
  notificationText: {
    fontWeight: 500,
    fontSize: 13,
    marginTop: 3,
  },
  errorText: {
    color: '#FFA150',
  },
  errorCircle: {
    fontSize: 18,
    color: '#FFA150',
    marginRight: 6,
  },
});

enum AvailableHoursModalTestID {
  Container = 'AvailableHoursModalTestID.Container',
  SaveButton = 'AvailableHoursModalTestID.SaveButton',
  WarningMessage = 'AvailableHoursModalTestID.WarningMessage',
}

interface Props {
  showDialog?: boolean;
  availableHourRanges?: AvailableHourRange[];
  updateAvailableHourRanges: (availableHourRanges: AvailableHourRange[]) => void;
  onCloseDialog: () => void;
}

export const updateAvailableHoursMutation = gql`
  mutation UpdateAvailableHoursMutation($availableHours: [AvailableHourInput]) {
    updateAvailableHours(availableHours: $availableHours) {
      ok
    }
  }
`;

export const AvailableHoursModal: React.FC<Props> = ({
  showDialog = true,
  availableHourRanges = [],
  updateAvailableHourRanges,
  onCloseDialog,
}) => {
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [unsavedHours, setUnsavedHours] = useState(availableHourRanges);
  const [shouldShowNoWorkingHoursError, setShouldShowNoWorkingHoursError] = useState(false);

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const onClose = () => {
    if (!availableHourRanges.length) {
      setShouldShowNoWorkingHoursError(true);
      return;
    }
    onCloseDialog();
  };

  const updateUnsavedHourRanges = (day: string, newRanges: AvailableHourRange[]) => {
    newRanges = unsavedHours.filter((hourRange) => hourRange.day !== day).concat(newRanges);
    setUnsavedHours(newRanges);
  };

  const saveHourRanges = () => {
    setSubmitted(true);

    if (!anyTimeRangeAdded || !timeRangesValid) {
      return;
    }

    setSaving(true);

    // Filter out the blank lines
    const hoursToSave = unsavedHours.filter((hour) => hour.start && hour.end);

    memberClient
      .mutate({
        mutation: updateAvailableHoursMutation,
        variables: { availableHours: hoursToSave },
      })
      .then(() => {
        enqueueSnackbar('Availability Saved', { variant: 'success' });
        updateAvailableHourRanges(hoursToSave);
        onClose();
        setSaving(false);
      })
      .catch(() => {
        enqueueSnackbar('Error Saving Availability', { variant: 'error' });
        setSaving(false);
      });
  };

  const isTimeRangeValid = (start?: string, end?: string) => {
    if (!start && !end) {
      return true;
    }

    const startDate = new Date('01/01/2000 ' + start);
    const endDate = new Date('01/01/2000 ' + end);

    return startDate < endDate;
  };

  const anyTimeRangeAdded = unsavedHours.filter(({ start, end }) => !!start && !!end).length > 0;
  const shouldShowError = shouldShowNoWorkingHoursError || (submitted && !anyTimeRangeAdded);

  const timeRangesValid = anyTimeRangeAdded
    ? unsavedHours.every(({ end, start }) => isTimeRangeValid(start, end))
    : true;

  useEffect(() => {
    if (availableHourRanges.length > 0) {
      setUnsavedHours(availableHourRanges);
      setShouldShowNoWorkingHoursError(false);
    }
  }, [availableHourRanges]);

  return (
    <Dialog
      open={showDialog}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      fullWidth={false}
      maxWidth="md"
      data-testid={AvailableHoursModalTestID.Container}
    >
      {shouldShowError ? (
        <div
          className={`${classes.notificationWrapper} ${classes.errorTextWrapper}`}
          data-testid={AvailableHoursModalTestID.WarningMessage}
        >
          <Error className={classes.errorCircle} />
          <Typography className={`${classes.notificationText} ${classes.errorText}`}>
            You must set Working Hours for clients to book sessions.
          </Typography>
        </div>
      ) : null}

      <DialogTitle id="form-dialog-title" className={classes.dialogTitle} disableTypography>
        <Typography className={classes.title}>Working Hours</Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.explanationText}>
          Set working hours to ensure clients can only book during the windows specified below.
          Clients can book sessions within 30 days.
        </Typography>
        <div className={classes.daysList}>
          {Object.keys(DAY_NAMES).map((dayName) => (
            <AvailableDayInput
              key={dayName}
              hours={unsavedHours.filter((hourRange) => hourRange.day === dayName)}
              dayName={dayName}
              isTimeRangeValid={isTimeRangeValid}
              onHoursUpdated={updateUnsavedHourRanges}
            />
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <MHButton
          children="Save"
          onClick={saveHourRanges}
          disabled={saving || !timeRangesValid}
          loading={saving}
          data-testid={AvailableHoursModalTestID.SaveButton}
        />
      </DialogActions>
    </Dialog>
  );
};
